import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import type { Callback, CallbackStatus, CallbackClassification } from "../features/callbacks/types";

function mapClassification(value: string): CallbackClassification {
  if (value === "Needs Callback") return "needs_callback";
  if (value === "Not Picked") return "not_picked";
  if (value === "Voicemail") return "voicemail";
  if (value === "Rescheduled") return "rescheduled";
  return "not_picked";
}

function mapStatus(value: string): CallbackStatus {
  if (value === "paused") return "paused";
  if (value === "pending") return "pending";
  if (value === "completed") return "completed";
  if (value === "failed") return "failed";
  if (value === "cancelled") return "cancelled";
  if (value === "needs_schedule_details") return "needs_schedule_details";
  if (value === "queued") return "scheduled";
  return "pending";
}

function mapCallback(item: any): Callback {
  return {
    id: item.call_id || item.schedule_id || Math.random().toString(),
    contactId: item.contact_id || item.call_id || "",
    contactName: item.contact_name || "Unknown Contact",
    phone: item.customer_phone || "",
    scheduleId: item.schedule_id || "",
    classification: mapClassification(item.classification),
    status: mapStatus(item.status),
    callbackDate: item.callback_date || "",
    callbackTime: item.callback_time || "",
    callbackType: item.callback_type || "",
    callbackValue: item.callback_value || "",
    timezone: item.callback_timezone || "Asia/Kolkata",
    aiSummary: item.summary || "",
    scheduledForUtc: item.scheduled_for_utc || null,
  };
}

import type { DateRangeValue } from "../components/ui/date-range-picker";

export function useCallbacks(dateRange?: DateRangeValue) {
  const { token } = useAuth();
  const [callbacks, setCallbacks] = useState<Callback[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const apiUrl = process.env.REACT_APP_BACKEND_URL || "";



  const fetchCallbacks = useCallback(async (targetPage: number, replace = false) => {
    try {
      setLoading(true);
      const params: any = { limit: 50, page: targetPage, view: "summary" };
      if (dateRange) {
        const formatDate = (d: Date) => {
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return `${y}-${m}-${day}`;
        };
        params.start_date = formatDate(dateRange.start);
        params.end_date = formatDate(dateRange.end);
      }

      const response = await axios.get(`${apiUrl}/api/v1/schedules/callbacks`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      const items = response.data?.items || response.data?.callbacks || [];
      const mapped = items.map(mapCallback);
      setCallbacks((current) => targetPage === 1 || replace ? mapped : [...current, ...mapped]);
      setHasMore(Boolean(response.data?.has_more));
      return mapped;
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [apiUrl, token, dateRange]);

  useEffect(() => {
    if (token) {
      fetchCallbacks(page).catch((error) => console.error("Failed to load callbacks", error));
    }
  }, [token, page, fetchCallbacks]);

  const loadMore = useCallback(() => {
    if (!loading && hasMore) setPage((current) => current + 1);
  }, [loading, hasMore]);

  const refresh = useCallback(async () => {
    setPage(1);
    await fetchCallbacks(1, true);
  }, [fetchCallbacks]);

  const updateStatus = useCallback(async (ids: string[], nextStatus: CallbackStatus) => {
    const previous = callbacks;
    setCallbacks((current) => current.map((callback) => (
      ids.includes(callback.id) ? { ...callback, status: nextStatus } : callback
    )));

    try {
      const responses = await Promise.all(ids.map((id) => axios.patch(
        `${apiUrl}/api/v1/schedules/callbacks/${encodeURIComponent(id)}/toggle-pause`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      )));
      const statusById = new Map(
        responses.map((response, index) => [
          ids[index],
          mapStatus(response.data?.new_status || nextStatus),
        ]),
      );
      setCallbacks((current) => current.map((callback) => (
        statusById.has(callback.id)
          ? { ...callback, status: statusById.get(callback.id)! }
          : callback
      )));
    } catch (error) {
      setCallbacks(previous);
      throw error;
    }
  }, [apiUrl, callbacks, token]);

  const deleteCallback = useCallback(async (id: string) => {
    const previous = callbacks;
    setCallbacks((current) => current.filter((callback) => callback.id !== id));
    try {
      await axios.delete(
        `${apiUrl}/api/v1/schedules/callbacks/${encodeURIComponent(id)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch (error) {
      setCallbacks(previous);
      throw error;
    }
  }, [apiUrl, callbacks, token]);

  const rescheduleCallback = useCallback(async (
    id: string,
    callbackDate: string,
    callbackTime: string,
    callbackTimezone: string,
  ) => {
    const previous = callbacks;
    setCallbacks((current) => current.map((callback) => (
      callback.id === id
        ? {
            ...callback,
            callbackDate,
            callbackTime,
            timezone: callbackTimezone,
            status: "pending",
            classification: "rescheduled",
          }
        : callback
    )));

    try {
      await axios.patch(
        `${apiUrl}/api/v1/schedules/callbacks/${encodeURIComponent(id)}`,
        {
          callback_date: callbackDate,
          callback_time: callbackTime,
          callback_timezone: callbackTimezone,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch (error) {
      setCallbacks(previous);
      throw error;
    }
  }, [apiUrl, callbacks, token]);

  return {
    callbacks,
    updateStatus,
    deleteCallback,
    rescheduleCallback,
    refresh,
    loading: initialLoad && loading,
    refreshing: !initialLoad && loading,
    hasMore,
    loadMore,
  };
}
